import React, { useState } from 'react';
import { Box, Tooltip, Typography } from '@mui/material';

import { ImageThumbnail } from '../../../components/design-system';
import type { PasFind } from '../../../components/FindPasEditDialog';

interface PasFindThumbnailProps {
  find: PasFind;
}

const IMAGE_DELIVERY_URL = 'https://imagedelivery.net/3zdDZwDRXumMqC2LPbSSXw';
const AMCR_IMAGE_URL = 'https://digiarchiv.aiscr.cz/api/img';
const THUMBNAIL_SIZE = 40;

const triggerStyles = { display: 'inline-flex' };
const emptyStyles = { color: 'text.secondary' };
const previewStyles = {
  display: 'block',
  width: 375,
  maxWidth: '90vw',
  height: 'auto',
};
const tooltipSlotProps = {
  tooltip: { sx: { maxWidth: 'none', p: 0.5 } },
};

const getImageSources = (find: PasFind) => {
  const imageId = find.images?.[0] || find.image;

  if (imageId) {
    return {
      thumbnail: `${IMAGE_DELIVERY_URL}/${imageId}/w=80`,
      preview: `${IMAGE_DELIVERY_URL}/${imageId}/w=750`,
    };
  }

  const amcrImageId = find.amcrData?.images?.[0];

  if (amcrImageId) {
    return {
      thumbnail: `${AMCR_IMAGE_URL}/thumb?id=${amcrImageId}`,
      preview: `${AMCR_IMAGE_URL}/medium?size=medium&id=${amcrImageId}`,
    };
  }

  return null;
};

/** The larger photo loads only while the tooltip is open. */
const PasFindThumbnail = React.memo(({ find }: PasFindThumbnailProps) => {
  const [open, setOpen] = useState(false);
  const sources = getImageSources(find);

  const handleOpen = () => {
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  if (!sources) {
    return <Typography sx={emptyStyles}>—</Typography>;
  }

  const preview = open ? (
    <Box component="img" alt="" src={sources.preview} sx={previewStyles} />
  ) : (
    ''
  );

  return (
    <Tooltip
      open={open}
      onOpen={handleOpen}
      onClose={handleClose}
      placement="right"
      slotProps={tooltipSlotProps}
      title={preview}
    >
      <Box sx={triggerStyles}>
        <ImageThumbnail alt="" size={THUMBNAIL_SIZE} src={sources.thumbnail} />
      </Box>
    </Tooltip>
  );
});

PasFindThumbnail.displayName = 'PasFindThumbnail';

export { PasFindThumbnail };
